import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Pencil } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { base44 } from "@/api/base44Client";
import EnrichmentModal from "@/pages/manchester/EnrichmentModal";
import EditPostModal from "@/pages/resources/EditPostModal";

const slugify = (text) =>
  text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function DynamicResourceArticle({ slug }) {
  const [post, setPost] = useState(undefined); // undefined = loading, null = not found
  const [showModal, setShowModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    let active = true;
    base44.entities.ResourcePost.filter({ slug }).then((results) => {
      if (!active) return;
      const found = results?.find((p) => p.indexable !== false) || results?.[0] || null;
      setPost(found);
    }).catch(() => { if (active) setPost(null); });
    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    base44.auth.me().then(user => setIsAdmin(user?.role === "admin")).catch(() => setIsAdmin(false));
  }, []);

  useEffect(() => {
    if (!post) return;
    document.title = `${post.title} | Truestays`;
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.name = name; document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", post.meta_description || "");
    setMeta("robots", "index, follow");
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = `https://www.truestays.co.uk/resources/${post.slug}`;

    // Extract FAQ Q&A pairs from the body: the "Frequently asked questions" heading
    // followed by h2(question) + p(answer) pairs.
    const body = post.body || [];
    const faqStart = body.findIndex(b => b.type === "h2" && /frequently asked/i.test(b.text || ""));
    const faqs = [];
    if (faqStart >= 0) {
      for (let j = faqStart + 1; j < body.length; j++) {
        const b = body[j];
        if (b.type === "h2") {
          const next = body[j + 1];
          if (next && next.type === "p") faqs.push({ q: b.text, a: next.text });
        }
      }
    }

    const injectScript = (id, json) => {
      let s = document.getElementById(id);
      if (s) s.remove();
      s = document.createElement("script");
      s.id = id;
      s.type = "application/ld+json";
      s.text = JSON.stringify(json);
      document.head.appendChild(s);
    };

    injectScript("truestays-article-schema", {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      datePublished: post.publish_date,
      author: { "@type": "Organization", name: "Truestays Management Ltd" },
      publisher: { "@type": "Organization", name: "Truestays Management Ltd" },
      description: post.meta_description || "",
      mainEntityOfPage: `https://www.truestays.co.uk/resources/${post.slug}`,
    });

    if (faqs.length) {
      injectScript("truestays-faq-schema", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map(f => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    }

    return () => {
      ["truestays-article-schema", "truestays-faq-schema"].forEach(id => {
        const s = document.getElementById(id);
        if (s) s.remove();
      });
    };
  }, [post]);

  const handleTocClick = (e, item) => {
    e.preventDefault();
    const el = document.getElementById(slugify(item));
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 84;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  if (post === undefined) {
    return <div style={{ paddingTop: 140, minHeight: "60vh" }} />;
  }

  if (post === null) {
    return (
      <div style={{ paddingTop: 120, textAlign: "center", fontFamily: "'DM Sans', sans-serif", color: "#888" }}>
        <p>Article not found.</p>
        <Link to="/resources" style={{ color: "#4A6DC7" }}>Back to resources</Link>
      </div>
    );
  }

  const dateLabel = post.publish_date
    ? new Date(post.publish_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : "";

  return (
    <div>
      <div style={{ backgroundColor: "#161616", paddingTop: "clamp(108px, 9vw, 140px)", paddingBottom: "clamp(40px, 5vw, 56px)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", paddingLeft: "clamp(20px, 5vw, 80px)", paddingRight: "clamp(20px, 5vw, 80px)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: "clamp(24px, 3vw, 40px)" }}>
            <Link to="/resources" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "rgba(255,255,255,0.6)", textDecoration: "none", display: "flex", alignItems: "center", gap: 4 }}>
              <ArrowLeft size={13} /> Resources
            </Link>
            {isAdmin && (
              <button onClick={() => setShowEditModal(true)} style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 100, padding: "7px 16px", color: "#fff", fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                <Pencil size={13} /> Edit article
              </button>
            )}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: post.img ? "1.05fr 0.95fr" : "1fr", gap: "clamp(32px, 5vw, 64px)", alignItems: "center" }} className="article-hero-grid">
            <div>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", color: "rgba(255,255,255,0.5)", textTransform: "uppercase", marginBottom: 16, marginTop: 0 }}>{post.tag}</p>
              <h1 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "clamp(28px, 4vw, 46px)", lineHeight: 1.12, letterSpacing: "-0.03em", color: "#fff", marginTop: 0, marginBottom: 24 }}>{post.title}</h1>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <img src="https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/52e98417f_Untitleddesign37.jpg" alt="Truestays" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", background: "#fff", padding: 4 }} />
                <div>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 13, color: "#fff", margin: 0 }}>Truestays Team</p>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "rgba(255,255,255,0.5)", margin: "2px 0 0" }}>{dateLabel}</p>
                </div>
              </div>
            </div>
            {post.img && (
              <div style={{ borderRadius: 20, overflow: "hidden", aspectRatio: "4 / 3" }}>
                <img src={post.img} alt={post.title} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ backgroundColor: "#fff", paddingTop: "clamp(40px, 5vw, 64px)", paddingBottom: "clamp(60px, 7vw, 96px)", paddingLeft: "clamp(20px, 5vw, 80px)", paddingRight: "clamp(20px, 5vw, 80px)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "260px 1fr", gap: 72, alignItems: "start" }} className="article-body-grid">
          <div className="article-sidebar" style={{ position: "sticky", top: 88 }}>
            {post.toc?.length > 0 && (
              <>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#888", marginBottom: 16, marginTop: 0 }}>In this article</p>
                <div style={{ borderTop: "1px solid #E5E5E5", paddingTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
                  {post.toc.map((item, i) => (
                    <a key={i} href={`#${slugify(item)}`} onClick={(e) => handleTocClick(e, item)}
                      style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13.5, color: "#4A4A4A", margin: 0, lineHeight: 1.5, textDecoration: "none", cursor: "pointer" }}>
                      {item}
                    </a>
                  ))}
                </div>
              </>
            )}
            <div style={{ marginTop: 40, background: "linear-gradient(135deg, #112215 0%, #1A3A2A 100%)", borderRadius: 14, padding: "24px 20px", color: "#fff" }}>
              <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 16, marginBottom: 8, marginTop: 0, lineHeight: 1.3 }}>Get a free income estimate</p>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "rgba(255,255,255,0.65)", margin: "0 0 16px", lineHeight: 1.5 }}>Find out what your property could earn. No commitment required.</p>
              <button onClick={() => setShowModal(true)} style={{ background: "hsl(var(--primary))", color: "#fff", border: "none", borderRadius: 8, padding: "11px 16px", fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", width: "100%" }}>
                Get started
              </button>
            </div>
          </div>

          <div style={{ maxWidth: 680 }}>
            {post.body?.map((block, i) => {
              if (block.type === "p") return (
                <div key={i} className="markdown-content" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#333", lineHeight: 1.75, margin: "0 0 20px" }}>
                  <ReactMarkdown>{block.text}</ReactMarkdown>
                </div>
              );
              if (block.type === "h2") return <h2 key={i} id={slugify(block.text)} style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(20px, 2vw, 26px)", letterSpacing: "-0.02em", color: "#1A1A1A", margin: "44px 0 16px", lineHeight: 1.25, scrollMarginTop: 84 }}>{block.text}</h2>;
              if (block.type === "ul") return (
                <ul key={i} className="markdown-content" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#333", lineHeight: 1.75, margin: "0 0 20px 0", paddingLeft: 24 }}>
                  {block.items?.map((item, ii) => (
                    <li key={ii} style={{ marginBottom: 10 }}><ReactMarkdown>{item}</ReactMarkdown></li>
                  ))}
                </ul>
              );
              if (block.type === "table" && block.rows?.length) return (
                <div key={i} style={{ overflowX: "auto", margin: "24px 0", maxWidth: "100%" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "'DM Sans', sans-serif", fontSize: 14 }}>
                    {block.rows.map((row, ri) => (
                      <tr key={ri} style={{ borderBottom: "1px solid #E5E5E5" }}>
                        {row.map((cell, ci) => ri === 0 ? (
                          <th key={ci} style={{ padding: "10px 16px", textAlign: "left", fontWeight: 700, color: "#333", background: "#F7F7F7", whiteSpace: "nowrap" }}>{cell}</th>
                        ) : (
                          <td key={ci} style={{ padding: "10px 16px", color: ci === 0 ? "#333" : "#555" }}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </table>
                </div>
              );
              return null;
            })}

            <div style={{ marginTop: 48, background: "#F7F7F7", borderRadius: 14, padding: "32px 28px", display: "flex", flexDirection: "column", gap: 12 }}>
              <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 20, color: "#1A1A1A", margin: 0 }}>Ready to get started?</p>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#555", margin: 0, lineHeight: 1.6 }}>Get a free personalised income estimate for your property. No commitment, no pressure.</p>
              <button onClick={() => setShowModal(true)} style={{ alignSelf: "flex-start", background: "hsl(var(--primary))", color: "#fff", border: "none", borderRadius: 8, padding: "13px 24px", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                Get my free estimate <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {showModal && <EnrichmentModal mode="full" onClose={() => setShowModal(false)} onSubmitted={() => setShowModal(false)} />}
      {showEditModal && (
        <EditPostModal
          post={post}
          onClose={() => setShowEditModal(false)}
          onSaved={(updated) => { setPost(updated); setShowEditModal(false); }}
        />
      )}

      <style>{`
        .markdown-content > p { margin: 0 0 16px; }
        .markdown-content ul { margin: 0 0 20px 0; padding-left: 24px; list-style-type: disc; }
        .markdown-content li { margin-bottom: 10px; line-height: 1.65; }
        .markdown-content strong { font-weight: 700; color: #1A1A1A; }
        .markdown-content a { color: #4A6DC7; }
        @media (max-width: 900px) {
          .article-hero-grid { grid-template-columns: 1fr !important; }
          .article-body-grid { grid-template-columns: 1fr !important; }
          .article-sidebar { position: static !important; }
        }
      `}</style>
    </div>
  );
}